import React, { useState } from 'react';
import {
  Typography,
  Box,
  Button,
  TextField,
  List,
  ListItem,
  ListItemText,
  IconButton,
  Divider,
  Paper,
  Grid,
  Slider,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Alert,
  ListItemAvatar,
  Avatar,
  Chip
} from '@mui/material';
import {
  Add as AddIcon,
  Delete as DeleteIcon,
  Edit as EditIcon,
  ArrowUpward as ArrowUpwardIcon,
  ArrowDownward as ArrowDownwardIcon,
  Layers as LayersIcon,
  ArrowForward as ArrowForwardIcon,
  ArrowBack as ArrowBackIcon
} from '@mui/icons-material';
import { useAppContext } from '../context/AppContext';
import { Layer } from '../types';
import ImageUpload from './ImageUpload';

// Define BlendMode type directly here since it's not being properly imported
type BlendMode = 
  | 'source-over' | 'source-in' | 'source-out' | 'source-atop'
  | 'destination-over' | 'destination-in' | 'destination-out' | 'destination-atop'
  | 'lighter' | 'copy' | 'xor' | 'multiply' | 'screen' | 'overlay'
  | 'darken' | 'lighten' | 'color-dodge' | 'color-burn' | 'hard-light'
  | 'soft-light' | 'difference' | 'exclusion' | 'hue' | 'saturation'
  | 'color' | 'luminosity';

const LayerSetup: React.FC = () => {
  const { layers, addLayer, removeLayer, updateLayer, setCurrentStep, setLayers } = useAppContext();
  const [newLayerName, setNewLayerName] = useState('');
  const [editLayerId, setEditLayerId] = useState<number | null>(null);
  const [editLayerName, setEditLayerName] = useState('');
  const [editLayerBlend, setEditLayerBlend] = useState<BlendMode>('source-over');
  const [editLayerOpacity, setEditLayerOpacity] = useState(1);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [currentLayer, setCurrentLayer] = useState<Layer | null>(null);

  const handleAddLayer = () => {
    if (newLayerName.trim()) {
      addLayer(newLayerName.trim(), 'source-over');
      setNewLayerName('');
    }
  };

  const handleOpenEditDialog = (layer: Layer) => {
    setEditLayerId(layer.id);
    setEditLayerName(layer.name);
    setEditLayerBlend(layer.blend as BlendMode);
    setEditLayerOpacity(layer.opacity);
    setCurrentLayer(layer);
    setDialogOpen(true);
  };

  const handleCloseEditDialog = () => {
    setDialogOpen(false);
    setEditLayerId(null);
    setCurrentLayer(null);
  };

  const handleSaveLayer = () => {
    if (editLayerId !== null && editLayerName.trim()) {
      updateLayer(editLayerId, {
        name: editLayerName.trim(),
        blend: editLayerBlend,
        opacity: editLayerOpacity
      });
      handleCloseEditDialog();
    }
  };

  const handleMoveLayer = (index: number, direction: 'up' | 'down') => {
    if (
      (direction === 'up' && index === 0) ||
      (direction === 'down' && index === layers.length - 1)
    ) {
      return;
    }

    const newLayers = [...layers];
    const targetIndex = direction === 'up' ? index - 1 : index + 1;
    
    [newLayers[index], newLayers[targetIndex]] = [newLayers[targetIndex], newLayers[index]];
    
    // Update the layers order
    setLayers(newLayers);
  };

  const handleNext = () => {
    if (layers.length > 0) {
      setCurrentStep(2);
    }
  };

  const handleBack = () => {
    setCurrentStep(0);
  };

  return (
    <Box>
      <Box display="flex" alignItems="center" mb={3}>
        <LayersIcon color="primary" sx={{ mr: 1 }} />
        <Typography variant="h5" component="h1">
          Layer Setup
        </Typography>
      </Box>

      <Divider sx={{ mb: 4 }} />

      <Paper elevation={3} sx={{ p: 3, mb: 4 }}>
        <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
          <Typography variant="h6">
            Layers ({layers.length})
          </Typography>
          
          <Box display="flex" alignItems="center">
            <TextField
              label="Layer Name"
              value={newLayerName}
              onChange={(e) => setNewLayerName(e.target.value)}
              size="small"
              sx={{ mr: 2 }}
              onKeyPress={(e) => {
                if (e.key === 'Enter') {
                  handleAddLayer();
                }
              }}
            />
            <Button
              variant="contained"
              color="primary"
              startIcon={<AddIcon />}
              onClick={handleAddLayer}
              disabled={!newLayerName.trim()}
            >
              Add
            </Button>
          </Box>
        </Box>
        
        {layers.length === 0 ? (
          <Alert severity="info" sx={{ mt: 2 }}>
            No layers yet. Add your first layer to get started.
          </Alert>
        ) : (
          <List>
            {layers.map((layer, index) => (
              <React.Fragment key={layer.id}>
                {index > 0 && <Divider />}
                <ListItem
                  secondaryAction={
                    <Box>
                      <IconButton 
                        edge="end" 
                        aria-label="move up"
                        onClick={() => handleMoveLayer(index, 'up')}
                        disabled={index === 0}
                      >
                        <ArrowUpwardIcon />
                      </IconButton>
                      <IconButton 
                        edge="end" 
                        aria-label="move down"
                        onClick={() => handleMoveLayer(index, 'down')}
                        disabled={index === layers.length - 1}
                      >
                        <ArrowDownwardIcon />
                      </IconButton>
                      <IconButton 
                        edge="end" 
                        aria-label="delete"
                        onClick={() => removeLayer(layer.id)}
                      >
                        <DeleteIcon />
                      </IconButton>
                    </Box>
                  }
                >
                  <ListItemAvatar>
                    <Avatar sx={{ bgcolor: 'primary.main' }}>
                      <LayersIcon />
                    </Avatar>
                  </ListItemAvatar>
                  
                  <ListItemText
                    primary={
                      <Box display="flex" alignItems="center">
                        <TextField
                          value={layer.name}
                          onChange={(e) => updateLayer(layer.id, { name: e.target.value })}
                          variant="standard"
                          sx={{ mr: 2 }}
                        />
                        <Chip 
                          label={`${layer.elements.length} elements`} 
                          size="small" 
                          color={layer.elements.length > 0 ? "primary" : "default"}
                        />
                      </Box>
                    }
                    secondary={
                      <Box sx={{ mt: 1 }}>
                        <Grid container spacing={2} alignItems="center">
                          <Grid item xs={12}>
                            <Box component="span" sx={{ display: 'block', mb: 1, fontSize: '0.875rem' }}>
                              Opacity: {layer.opacity * 100}%
                            </Box>
                            <Slider
                              value={layer.opacity}
                              onChange={(_, value) => updateLayer(layer.id, { opacity: value as number })}
                              step={0.01}
                              min={0}
                              max={1}
                              valueLabelDisplay="auto"
                              valueLabelFormat={(value) => `${Math.round(value * 100)}%`}
                            />
                          </Grid>
                        </Grid>
                      </Box>
                    }
                    secondaryTypographyProps={{ component: 'div' }}
                  />
                </ListItem>
              </React.Fragment>
            ))}
          </List>
        )}
      </Paper>

      <Box display="flex" justifyContent="space-between" mt={4}>
        <Button
          variant="outlined"
          onClick={handleBack}
          startIcon={<ArrowBackIcon />}
        >
          Back
        </Button>
        
        <Button
          variant="contained"
          color="primary"
          onClick={handleNext}
          endIcon={<ArrowForwardIcon />}
          disabled={layers.length === 0}
        >
          Next: Image Upload
        </Button>
      </Box>

      {/* Layer Edit Dialog */}
      <Dialog open={dialogOpen} onClose={handleCloseEditDialog}>
        <DialogTitle>Edit Layer</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            margin="dense"
            label="Layer Name"
            fullWidth
            value={editLayerName}
            onChange={(e) => setEditLayerName(e.target.value)}
            sx={{ mb: 2 }}
          />
            
          <Box component="span" sx={{ display: 'block', mb: 1 }}>
            Opacity: {Math.round(editLayerOpacity * 100)}%
          </Box>
          <Slider
            value={editLayerOpacity}
            onChange={(_, value) => setEditLayerOpacity(value as number)}
            step={0.01}
            min={0}
            max={1}
            valueLabelDisplay="auto"
            valueLabelFormat={(value) => `${Math.round(value * 100)}%`}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseEditDialog}>Cancel</Button>
          <Button onClick={handleSaveLayer} color="primary" disabled={!editLayerName.trim()}>
            Save
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default LayerSetup; 